import { useTranslation } from "react-i18next"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"

import type { CustomerConfig } from "@/types/customer-config"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Skeleton } from "@/components/ui/skeleton"
import customerConfig from "@/customer/config.json"
import { getInjectedAppName } from "@/lib/runtime-branding"

const appName =
  getInjectedAppName() ?? (customerConfig as CustomerConfig).config.app_name ?? "Hello AI"

function replacePlaceholders(markdown: string): string {
  return markdown.replaceAll("{{appName}}", appName)
}

export type WhatsNewDialogShellProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Title text. Caller decides whether to append a version/locale suffix. */
  title: string
  /** Markdown body (preferred). */
  markdown?: string | null
  /** Legacy bullet items, retained for old payloads. Used only if `markdown` is absent. */
  items?: string[] | null
  /** Loading state from the data query. Drives the "no content yet" copy. */
  isLoading?: boolean
  /** Action callback fired when the user clicks the OK button. */
  onAcknowledge: () => void
  /** Optional fallback rendered when both `markdown` and `items` are empty. */
  emptyFallback?: React.ReactNode
}

/**
 * Pure presentational shell: Radix dialog + markdown rendering + OK button.
 *
 * Knows nothing about Zustand stores, user-settings writes, locale fetching,
 * or admin preview gating. Two callers (`WhatsNewDialog` for the user popup,
 * `WhatsNewPreviewDialog` for admin preview) layer those concerns on top.
 */
export function WhatsNewDialogShell({
  open,
  onOpenChange,
  title,
  markdown,
  items,
  isLoading = false,
  onAcknowledge,
  emptyFallback = null,
}: WhatsNewDialogShellProps) {
  const { t } = useTranslation()
  const hasContent = Boolean(markdown) || Boolean(items?.length)

  let body: React.ReactNode = null
  if (markdown) {
    body = (
      // No `rehype-raw` — markdown content here is loaded from blob storage
      // and rendering raw HTML would be a stored-XSS path if a malicious
      // payload ever landed in a What's New blob. CommonMark + GFM is enough
      // for release notes; if a future release ever needs HTML (e.g.
      // <details>), introduce `rehype-sanitize` with an explicit allowlist.
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{replacePlaceholders(markdown)}</ReactMarkdown>
    )
  } else if (items?.length) {
    body = items.map((item, idx) => <p key={idx}>{replacePlaceholders(item)}</p>)
  } else if (isLoading) {
    // Render a few skeleton lines while the query is in flight. Without
    // this, a manually-opened dialog (or the admin preview path) shows a
    // blank body until data lands. The auto-open user path doesn't hit
    // this branch — it only opens after `shouldShow` resolves — but the
    // shell shouldn't depend on that timing.
    body = (
      <div className="space-y-2">
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-11/12" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    )
  } else if (!hasContent) {
    body = emptyFallback
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-4xl">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogBody>
          <div className="prose dark:prose-invert max-w-none py-2">{body}</div>
        </AlertDialogBody>
        <AlertDialogFooter>
          <AlertDialogAction onClick={onAcknowledge}>{t("pages.common.ok")}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
